import { inject, Injectable } from '@angular/core';
import { AllowInElement, HotkeysService } from '@ngneat/hotkeys';
import { BehaviorSubject, distinctUntilChanged, filter, map } from 'rxjs';
import { tap } from 'rxjs/operators';

interface ShortcutConfig {
  keys: string;
  description: string;
  allowIn: AllowInElement[];
  action: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class HotkeyManagerService {
  private activeComponentsStack = new BehaviorSubject<string[]>([]);
  private shortcuts: Map<string, ShortcutConfig[]> = new Map();

  constructor(private hotkeys: HotkeysService) {
    // 스택의 마지막 컴포넌트가 변경될 때마다 단축키를 업데이트하도록 설정
    this.activeComponentsStack
      .pipe(
        map(stack => stack[stack.length - 1]),
        distinctUntilChanged(),
        filter(componentName => !!componentName),
        tap(componentName => {
          this.updateShortcuts(componentName);
        })
      )
      .subscribe();
  }

  activateHotkeysForComponent(
    componentName: string,
    shortcuts: ShortcutConfig[]
  ) {
    this.shortcuts.set(componentName, shortcuts);
    const stack = this.activeComponentsStack.getValue();
    if (!stack.includes(componentName)) {
      stack.push(componentName);
      this.activeComponentsStack.next(stack);
    }
  }

  deactivateHotkeys(componentName: string) {
    const stack = this.activeComponentsStack.getValue();
    const index = stack.indexOf(componentName);
    if (index !== -1) {
      stack.splice(index, 1);
      this.activeComponentsStack.next(stack);
    }
  }

  private updateShortcuts(componentName: string) {
    // 모든 단축키를 제거
    this.deactivateAllShortcuts();

    // 새로운 컴포넌트의 단축키를 활성화
    const shortcuts = this.shortcuts.get(componentName);
    if (shortcuts) {
      shortcuts.forEach(config => {
        this.hotkeys
          .addShortcut({
            keys: config.keys,
            description: config.description,
            allowIn: config.allowIn,
          })
          .subscribe(config.action);
      });
    }
  }

  private deactivateAllShortcuts() {
    this.shortcuts.forEach(configs => {
      configs.forEach(config => {
        this.hotkeys.removeShortcuts(config.keys);
      });
    });
  }
}

export function injectHotkeyManagerService() {
  return inject(HotkeyManagerService);
}
